import {
  AvaaioLanguageCode,
  AVAAIO_LANGUAGES,
  getInitialPreferredLanguage,
  normalizeAvaaioLanguage,
  persistPreferredLanguage,
} from "../utils/avaaioLanguage";
import React, { useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';

const API_BASE =
  import.meta.env.VITE_API_BASE ||
  "https://api.avaaio.com";

type RegistrationCopy = {
  title: string;
  subtitle: string;
  nameLabel: string;
  nameTitle: string;
  namePlaceholder: string;
  emailLabel: string;
  emailTitle: string;
  passwordLabel: string;
  passwordTitle: string;
  zipLabel: string;
  zipTitle: string;
  languageLabel: string;
  languageTitle: string;
  note: string;
  securityLabel: string;
  captchaTitle: string;
  registerButton: string;
  existingAccount: string;
  loginButton: string;
  captchaRequired: string;
  success: string;
  failed: string;
  connectionError: string;
};

const REGISTER_COPY: Record<
  AvaaioLanguageCode,
  RegistrationCopy
> = {
  en: {
    title: "Create Your Account",
    subtitle:
      "Enter the AVAAIO ecosystem and begin your next-generation AI experience.",
    nameLabel: "Name:",
    nameTitle: "Full Name",
    namePlaceholder: "Your Name",
    emailLabel: "Email:",
    emailTitle: "Email Address",
    passwordLabel: "Password:",
    passwordTitle: "Password",
    zipLabel: "ZIP Code (optional):",
    zipTitle: "ZIP Code",
    languageLabel: "Preferred Language:",
    languageTitle: "Preferred Language",
    note:
      "This preference controls the AVAAIO interface and account email language. Multilingual Ava conversations remain a Pro feature.",
    securityLabel: "Security Check",
    captchaTitle: "CAPTCHA verification",
    registerButton: "Register",
    existingAccount: "Already have an account?",
    loginButton: "Login",
    captchaRequired:
      "❌ Please complete the reCAPTCHA.",
    success:
      "✅ Registration successful! Check your email for the invite code.",
    failed:
      "❌ Registration failed. Try again.",
    connectionError:
      "❌ Error connecting to server. Please try again.",
  },
  es: {
    title: "Crear tu cuenta",
    subtitle:
      "Entra en el ecosistema AVAAIO y comienza tu experiencia de IA de próxima generación.",
    nameLabel: "Nombre:",
    nameTitle: "Nombre completo",
    namePlaceholder: "Tu nombre",
    emailLabel: "Correo electrónico:",
    emailTitle: "Dirección de correo electrónico",
    passwordLabel: "Contraseña:",
    passwordTitle: "Contraseña",
    zipLabel: "Código postal (opcional):",
    zipTitle: "Código postal",
    languageLabel: "Idioma preferido:",
    languageTitle: "Idioma preferido",
    note:
      "Esta preferencia controla el idioma de la interfaz de AVAAIO y de los correos de la cuenta. Las conversaciones multilingües con Ava siguen siendo una función Pro.",
    securityLabel: "Comprobación de seguridad",
    captchaTitle: "Verificación CAPTCHA",
    registerButton: "Registrarse",
    existingAccount: "¿Ya tienes una cuenta?",
    loginButton: "Iniciar sesión",
    captchaRequired:
      "❌ Completa el reCAPTCHA.",
    success:
      "✅ Registro completado. Revisa tu correo para obtener el código de invitación.",
    failed:
      "❌ No se pudo completar el registro. Inténtalo de nuevo.",
    connectionError:
      "❌ Error al conectar con el servidor. Inténtalo de nuevo.",
  },
  fr: {
    title: "Créer votre compte",
    subtitle:
      "Entrez dans l’écosystème AVAAIO et commencez votre expérience d’IA de nouvelle génération.",
    nameLabel: "Nom :",
    nameTitle: "Nom complet",
    namePlaceholder: "Votre nom",
    emailLabel: "E-mail :",
    emailTitle: "Adresse e-mail",
    passwordLabel: "Mot de passe :",
    passwordTitle: "Mot de passe",
    zipLabel: "Code postal (facultatif) :",
    zipTitle: "Code postal",
    languageLabel: "Langue préférée :",
    languageTitle: "Langue préférée",
    note:
      "Cette préférence contrôle la langue de l’interface AVAAIO et des e-mails du compte. Les conversations multilingues avec Ava restent une fonctionnalité Pro.",
    securityLabel: "Contrôle de sécurité",
    captchaTitle: "Vérification CAPTCHA",
    registerButton: "S’inscrire",
    existingAccount: "Vous avez déjà un compte ?",
    loginButton: "Se connecter",
    captchaRequired:
      "❌ Veuillez compléter le reCAPTCHA.",
    success:
      "✅ Inscription réussie. Consultez votre e-mail pour obtenir le code d’invitation.",
    failed:
      "❌ Échec de l’inscription. Veuillez réessayer.",
    connectionError:
      "❌ Erreur de connexion au serveur. Veuillez réessayer.",
  },
  de: {
    title: "Konto erstellen",
    subtitle:
      "Betreten Sie das AVAAIO-Ökosystem und beginnen Sie Ihr KI-Erlebnis der nächsten Generation.",
    nameLabel: "Name:",
    nameTitle: "Vollständiger Name",
    namePlaceholder: "Ihr Name",
    emailLabel: "E-Mail:",
    emailTitle: "E-Mail-Adresse",
    passwordLabel: "Passwort:",
    passwordTitle: "Passwort",
    zipLabel: "Postleitzahl (optional):",
    zipTitle: "Postleitzahl",
    languageLabel: "Bevorzugte Sprache:",
    languageTitle: "Bevorzugte Sprache",
    note:
      "Diese Einstellung steuert die Sprache der AVAAIO-Oberfläche und der Konto-E-Mails. Mehrsprachige Gespräche mit Ava bleiben eine Pro-Funktion.",
    securityLabel: "Sicherheitsprüfung",
    captchaTitle: "CAPTCHA-Prüfung",
    registerButton: "Registrieren",
    existingAccount: "Sie haben bereits ein Konto?",
    loginButton: "Anmelden",
    captchaRequired:
      "❌ Bitte schließen Sie das reCAPTCHA ab.",
    success:
      "✅ Registrierung erfolgreich. Prüfen Sie Ihre E-Mail auf den Einladungscode.",
    failed:
      "❌ Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.",
    connectionError:
      "❌ Fehler bei der Verbindung zum Server. Bitte versuchen Sie es erneut.",
  },
  it: {
    title: "Crea il tuo account",
    subtitle:
      "Entra nell’ecosistema AVAAIO e inizia la tua esperienza di IA di nuova generazione.",
    nameLabel: "Nome:",
    nameTitle: "Nome completo",
    namePlaceholder: "Il tuo nome",
    emailLabel: "Email:",
    emailTitle: "Indirizzo email",
    passwordLabel: "Password:",
    passwordTitle: "Password",
    zipLabel: "CAP (facoltativo):",
    zipTitle: "CAP",
    languageLabel: "Lingua preferita:",
    languageTitle: "Lingua preferita",
    note:
      "Questa preferenza controlla la lingua dell’interfaccia AVAAIO e delle email dell’account. Le conversazioni multilingue con Ava restano una funzione Pro.",
    securityLabel: "Controllo di sicurezza",
    captchaTitle: "Verifica CAPTCHA",
    registerButton: "Registrati",
    existingAccount: "Hai già un account?",
    loginButton: "Accedi",
    captchaRequired:
      "❌ Completa il reCAPTCHA.",
    success:
      "✅ Registrazione completata. Controlla la tua email per il codice di invito.",
    failed:
      "❌ Registrazione non riuscita. Riprova.",
    connectionError:
      "❌ Errore di connessione al server. Riprova.",
  },
  pt: {
    title: "Crie sua conta",
    subtitle:
      "Entre no ecossistema AVAAIO e comece sua experiência de IA de próxima geração.",
    nameLabel: "Nome:",
    nameTitle: "Nome completo",
    namePlaceholder: "Seu nome",
    emailLabel: "E-mail:",
    emailTitle: "Endereço de e-mail",
    passwordLabel: "Senha:",
    passwordTitle: "Senha",
    zipLabel: "CEP (opcional):",
    zipTitle: "CEP",
    languageLabel: "Idioma preferido:",
    languageTitle: "Idioma preferido",
    note:
      "Esta preferência controla o idioma da interface do AVAAIO e dos e-mails da conta. As conversas multilíngues com Ava continuam sendo um recurso Pro.",
    securityLabel: "Verificação de segurança",
    captchaTitle: "Verificação CAPTCHA",
    registerButton: "Cadastre-se",
    existingAccount: "Já tem uma conta?",
    loginButton: "Entrar",
    captchaRequired:
      "❌ Conclua o reCAPTCHA.",
    success:
      "✅ Cadastro concluído. Verifique seu e-mail para obter o código de convite.",
    failed:
      "❌ Não foi possível concluir o cadastro. Tente novamente.",
    connectionError:
      "❌ Erro ao conectar ao servidor. Tente novamente.",
  },
};

export default function InviteRegister() {
  const inviteCode =
    new URLSearchParams(window.location.search).get("invite") || "";

  const [name, setName] = useState('');
  const [email, setEmail] = useState(
    () => new URLSearchParams(window.location.search).get("email") || ""
  );
  const [zip, setZip] = useState('');
  const [password, setPassword] = useState('');
  const [preferredLanguage, setPreferredLanguage] =
    useState(getInitialPreferredLanguage());
  const [captchaToken, setCaptchaToken] = useState('');
  const [message, setMessage] = useState('');
  const [formValid, setFormValid] = useState(false);
  const navigate = useNavigate();
  const copy = REGISTER_COPY[preferredLanguage];

  useEffect(() => {
    setFormValid(!!(name && email && password));
  }, [name, email, password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("🌐 DEBUG API_BASE:", API_BASE);
    if (!captchaToken) {
      setMessage(copy.captchaRequired);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/v1/auth/register`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          zip,
          password,
          captcha_token: captchaToken,
        invite_code: inviteCode,
          preferred_language: preferredLanguage,
        }),
      });

      const result = await response.json();
      console.log("✅ Registration response:", result);

      if (response.ok) {
        // __AVAAIO_CLEAR_AUTH_AFTER_REGISTRATION_V10__
        try {
          const registrationAuthKeys = [
            "ava_session_token",
            "ava_token",
            "token",
            "access_token",
            "authToken",
            "jwt",
            "auth_user",
            "ava_user",
            "user",
            "profile",
            "ava_profile",
            "user_profile",
            "ava_auth_me_cache_v1",
          ];

          for (const key of registrationAuthKeys) {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          }

          sessionStorage.setItem(
            "ava_logged_out",
            "1",
          );
        } catch {}

        persistPreferredLanguage(
          preferredLanguage
        );
        // AVAAIO registration viewport repair V9
        try {
          const activeElement = document.activeElement;

          if (activeElement instanceof HTMLElement) {
            activeElement.blur();
          }

          document.documentElement.dataset.avaaioRegistrationViewport =
            "reset-v9";

          const resetRegistrationViewport = () => {
            try {
              window.scrollTo({
                top: 0,
                left: 0,
                behavior: "auto",
              });
            } catch {
              window.scrollTo(0, 0);
            }

            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
          };

          resetRegistrationViewport();

          window.requestAnimationFrame(() => {
            resetRegistrationViewport();

            window.setTimeout(
              resetRegistrationViewport,
              120,
            );
          });
        } catch {}

        setMessage(
          result?.email_sent === false
            ? "Your account was created, but the verification email could not be sent. Please contact AVAAIO support."
            : "Registration successful. Check your email and click the verification link before signing in."
        );

        setTimeout(
          () =>
            requestAnimationFrame(() =>
              navigate(
                `/login?verification=pending&email=${encodeURIComponent(
                  email
                )}`
              )
            ),
          4000
        );
      } else {
        const apiMessage =
          typeof result.detail === "string"
            ? result.detail
            : result.detail?.message ||
              result.error ||
              copy.failed;

        setMessage(apiMessage);
      }
    } catch (err) {
      console.error("❌ Registration error:", err);
      setMessage(copy.connectionError);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const frame = document.querySelector(
        'iframe[title=""]'
      );

      if (frame) {
        frame.setAttribute(
          "title",
          copy.captchaTitle
        );
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [copy.captchaTitle]);

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes registerGridMove {
          0% { background-position: 0 0, 0 0; }
          100% { background-position: 0 80px, 80px 0; }
        }

        @keyframes registerLedSweep {
          0% { transform: translateX(-130%) skewX(-18deg); opacity: 0; }
          20% { opacity: .35; }
          55% { opacity: .10; }
          100% { transform: translateX(140%) skewX(-18deg); opacity: 0; }
        }

        @keyframes registerGlowPulse {
          0%, 100% { opacity: .14; }
          50% { opacity: .32; }
        }
      `}</style>

      <div style={styles.gridLayer} />
      <div style={styles.ledSweepOne} />
      <div style={styles.ledSweepTwo} />
      <div style={styles.vignetteLayer} />

      <div style={styles.cardShell}>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.header}>
            <div style={styles.brand}>AVAAIO</div>
            <h1 style={styles.title}>
              {copy.title}
            </h1>
            <p style={styles.subtitle}>
              {copy.subtitle}
            </p>
          </div>

          <label
            htmlFor="name"
            style={styles.label}
          >
            {copy.nameLabel}
          </label>
          <input
            id="name"
            title={copy.nameTitle}
            placeholder={copy.namePlaceholder}
            type="text"
            required
            value={name}
            onChange={(event) =>
              setName(event.target.value)
            }
            style={inputStyle}
          />

          <label
            htmlFor="email"
            style={styles.label}
          >
            {copy.emailLabel}
          </label>
          <input
            id="email"
            title={copy.emailTitle}
            placeholder="you@example.com"
            type="email"
            required
            value={email}
            onChange={(event) =>
              setEmail(event.target.value)
            }
            style={inputStyle}
          />

          <label
            htmlFor="password"
            style={styles.label}
          >
            {copy.passwordLabel}
          </label>
          <input
            id="password"
            title={copy.passwordTitle}
            placeholder="********"
            type="password"
            required
            value={password}
            onChange={(event) =>
              setPassword(event.target.value)
            }
            style={inputStyle}
          />

          <label
            htmlFor="zip"
            style={styles.label}
          >
            {copy.zipLabel}
          </label>
          <input
            id="zip"
            title={copy.zipTitle}
            placeholder=""
            type="text"
            value={zip}
            onChange={(event) =>
              setZip(event.target.value)
            }
            style={inputStyle}
          />

          <label
            htmlFor="preferred-language"
            style={styles.label}
          >
            {copy.languageLabel}
          </label>

          <select
            id="preferred-language"
            title={copy.languageTitle}
            value={preferredLanguage}
            onChange={(event) => {
              const language =
                normalizeAvaaioLanguage(
                  event.target.value
                );

              setPreferredLanguage(language);
              persistPreferredLanguage(language);
              setCaptchaToken("");
              setMessage("");
            }}
            style={{
              ...inputStyle,
              colorScheme: "dark",
              background: "#08111f",
            }}
          >
            {AVAAIO_LANGUAGES.map((language) => (
              <option
                key={language.code}
                value={language.code}
                style={{
                  background: "#08111f",
                  color: "#ffffff",
                }}
              >
                {language.nativeLabel}
              </option>
            ))}
          </select>

          <div
            style={{
              color: "#9fc8d8",
              fontSize: "0.78rem",
              lineHeight: 1.5,
            }}
          >
            {copy.note}
          </div>

          <div style={styles.captchaWrap}>
            <ReCAPTCHA
              key={preferredLanguage}
              sitekey={
                import.meta.env
                  .VITE_RECAPTCHA_SITE_KEY
              }
              hl={preferredLanguage}
              onChange={(token) =>
                setCaptchaToken(token || "")
              }
              aria-label={copy.securityLabel}
              title={copy.captchaTitle}
              onExpired={() =>
                setCaptchaToken("")
              }
            />
          </div>

          <button
            type="submit"
            disabled={!captchaToken}
            style={{
              ...styles.submitButton,
              background: captchaToken
                ? 'linear-gradient(135deg,#00f0ff 0%,#38bdf8 100%)'
                : '#334155',
              cursor: captchaToken ? 'pointer' : 'not-allowed',
              boxShadow: captchaToken ? '0 0 22px rgba(0,240,255,0.45)' : 'none',
            }}
          >
            {copy.registerButton}
          </button>

          {message && (
            <p style={{
              ...styles.message,
              color: message.includes('✅') ? '#22ff88' : '#ff5f7a'
            }}>
              {message}
            </p>
          )}

          <p style={styles.loginText}>
            {copy.existingAccount}{" "}
            <button
              type="button"
              onClick={() => navigate("/login")}
              style={styles.loginButton}
            >
              {copy.loginButton}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    background: '#000000',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem 1rem',
    position: 'relative',
    overflow: 'hidden',
  },
  gridLayer: {
    position: 'absolute',
    inset: '-18%',
    backgroundColor: '#020607',
    backgroundImage: `
      radial-gradient(circle, rgba(0,255,255,0.95) 0 2px, transparent 2.5px),
      radial-gradient(circle, rgba(56,189,248,0.80) 0 1.5px, transparent 2px),
      linear-gradient(90deg, transparent 0 18px, rgba(0,255,255,0.50) 18px 20px, transparent 20px 42px),
      linear-gradient(0deg, transparent 0 18px, rgba(0,255,255,0.42) 18px 20px, transparent 20px 42px),
      linear-gradient(90deg, rgba(0,255,255,0.14) 1px, transparent 1px),
      linear-gradient(0deg, rgba(0,255,255,0.14) 1px, transparent 1px)
    `,
    backgroundSize: '84px 84px, 126px 126px, 84px 84px, 126px 126px, 42px 42px, 42px 42px',
    backgroundPosition: '0 0, 42px 42px, 0 21px, 21px 0, 0 0, 0 0',
    transform: 'perspective(760px) rotateX(58deg) scale(1.7)',
    transformOrigin: 'center bottom',
    top: '22%',
    opacity: 0.95,
    filter: 'drop-shadow(0 0 16px rgba(0,255,255,0.82))',
    pointerEvents: 'none',
  },
  ledSweepOne: {
    position: 'absolute',
    left: '-20%',
    top: '42%',
    width: '140%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, rgba(0,255,255,0.85), transparent)',
    boxShadow: '0 0 22px rgba(0,255,255,0.55)',
    pointerEvents: 'none',
    animation: 'registerLedSweep 4.8s ease-in-out infinite',
  },
  ledSweepTwo: {
    position: 'absolute',
    left: '-20%',
    top: '62%',
    width: '140%',
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(56,189,248,0.60), transparent)',
    boxShadow: '0 0 18px rgba(56,189,248,0.45)',
    pointerEvents: 'none',
    animation: 'registerLedSweep 7.2s ease-in-out infinite reverse',
  },
  vignetteLayer: {
    position: 'absolute',
    inset: 0,
    background: `
      radial-gradient(circle at 50% 0%, rgba(0,240,255,0.16), transparent 30%),
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.22) 44%, rgba(0,0,0,0.82) 100%)
    `,
    pointerEvents: 'none',
    animation: 'registerGlowPulse 5.5s ease-in-out infinite',
  },
  cardShell: {
    position: 'relative',
    zIndex: 2,
    border: '1px solid rgba(0,240,255,0.35)',
    background: 'rgba(5,10,25,0.84)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    boxShadow: '0 0 45px rgba(0,240,255,0.22), inset 0 0 20px rgba(0,240,255,0.08)',
    borderRadius: '28px',
    width: '100%',
    maxWidth: '500px',
    padding: '1.15rem',
    boxSizing: 'border-box',
  },
  form: {
    background: 'linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(2,6,23,0.98) 100%)',
    padding: '2rem',
    borderRadius: '22px',
    border: '1px solid rgba(255,255,255,0.05)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.55), 0 0 25px rgba(0,240,255,0.10)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.95rem',
    color: '#fff',
  },
  header: {
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  brand: {
    fontSize: '0.85rem',
    letterSpacing: '0.35rem',
    color: '#67e8f9',
    opacity: 0.9,
    marginBottom: '0.5rem',
    fontWeight: 800,
  },
  title: {
    margin: 0,
    fontSize: '2rem',
    fontWeight: 900,
    lineHeight: 1.1,
    color: '#ffffff',
    textShadow: '0 0 18px rgba(0,240,255,0.22)',
  },
  subtitle: {
    marginTop: '0.85rem',
    color: '#b6d7ff',
    fontSize: '0.95rem',
    lineHeight: 1.5,
  },
  label: {
    color: '#d8f7ff',
    fontWeight: 700,
    fontSize: '0.92rem',
  },
  captchaWrap: {
    width: '100%',
    minHeight: '160px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '1rem',
    marginBottom: '0.75rem',
    position: 'relative',
    zIndex: 1,
  },
  submitButton: {
    color: '#001018',
    fontWeight: 900,
    letterSpacing: '0.04rem',
    padding: '1rem',
    border: 'none',
    marginTop: '0.75rem',
    position: 'relative',
    zIndex: 2,
    borderRadius: '14px',
    transition: 'all 0.25s ease',
    fontSize: '1rem',
  },
  message: {
    textAlign: 'center',
    marginTop: '1rem',
    fontWeight: 700,
  },
  loginText: {
    textAlign: 'center',
    margin: '0.25rem 0 0',
    color: '#cfefff',
  },
  loginButton: {
    background: 'transparent',
    border: 'none',
    color: '#00f0ff',
    cursor: 'pointer',
    fontWeight: 800,
    textDecoration: 'underline',
  },
};

const inputStyle: React.CSSProperties = {
  padding: '0.95rem 1rem',
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.04)',
  color: '#ffffff',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
  fontSize: '0.96rem',
  transition: 'all 0.25s ease',
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.02)',
};
